// src/services/PlayerService.ts - UPDATED
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../config/firebase';
import { PlayerStats } from '../types';

// The curve lives in one place, shared with functions/src/levels.ts. Re-exported here
// because roughly fifty call sites import it from this module.
import { calculateExpForLevel, calculateLevelFromExp } from '../utils/levels';
export { calculateExpForLevel, calculateLevelFromExp };

// Get experience progress for current level
export const getExpProgress = (totalExp: number): { current: number; needed: number; percentage: number } => {
    const level = calculateLevelFromExp(totalExp);
    const currentLevelExp = calculateExpForLevel(level);
    const nextLevelExp = calculateExpForLevel(level + 1);
    const current = totalExp - currentLevelExp;
    const needed = nextLevelExp - currentLevelExp;
    const percentage = (current / needed) * 100;

    return {
        current,
        needed,
        percentage
    };
};

/**
 * The character for the current world, created on the server if it does not exist yet.
 *
 * The browser used to write the first `playerStats` document itself, and the rules let it on five
 * conditions while the other nineteen frozen fields were frozen on update only -- so a new account
 * could arrive already holding shares, matured deposits and level-999 attributes. Since 55.20 the
 * document is created by `createCharacter`, the rules refuse the browser the create, and the
 * starting shape lives in exactly one place (functions/src/character.ts).
 *
 * The callable is idempotent, so calling it for a character that already exists is a read. The
 * page renders from the listener that follows, so nothing is returned.
 */
export const ensureCharacter = async (server: string, documentId: string): Promise<void> => {
    const statsRef = doc(firestore, 'playerStats', documentId);

    if ((await getDoc(statsRef)).exists()) return;

    const call = httpsCallable<{ server: string }, { success: boolean; created: boolean }>(
        functions, 'createCharacter'
    );
    await call({ server });
};

// UPDATED: Now accepts optional documentId for server-specific retrieval
export const getPlayerStats = async (userId: string, documentId?: string): Promise<PlayerStats | null> => {
    const docId = documentId || userId;
    const statsRef = doc(firestore, 'playerStats', docId);
    const statsDoc = await getDoc(statsRef);

    if (statsDoc.exists()) {
        return statsDoc.data() as PlayerStats;
    }

    return null;
};